import { useMemo } from "react";
import { useSepaBankAccounts } from "../../../../stores/Sepa/sepa.queries";
import {
  MandateSummaryDto,
  MandateStatusEnum,
  MandateSequenceTypeEnum,
} from "../../../../stores/Sepa/sepa.types";

/** A mandate enriched with its bank account's holder + IBAN, for human-friendly pickers. */
export interface RcurMandateOption extends MandateSummaryDto {
  bankHolderFirstName: string;
  bankHolderLastName: string;
  bankIban: string;
}

/**
 * ACTIVE recurring (RCUR) mandates for a patient that match a given creditor account.
 *
 * Shared by the bill-row SEPA actions so the "per Lastschrift" button and the
 * surrounding action layout react to the same mandate availability. The underlying
 * query is keyed per patient, so calling this from multiple rows issues a single
 * request (react-query dedup). Each result carries its bank holder + IBAN so the
 * mandate picker can identify which account will be debited.
 */
export const useActiveRcurMandates = (
  patientId: number | undefined,
  accountId: string,
): RcurMandateOption[] => {
  const { data: bankAccounts = [] } = useSepaBankAccounts(patientId ?? 0, patientId != null);

  return useMemo(
    () =>
      bankAccounts.flatMap((account) =>
        account.mandates
          .filter(
            (mandate) =>
              mandate.status === MandateStatusEnum.Active &&
              mandate.sequenceType === MandateSequenceTypeEnum.Rcur &&
              mandate.accountId === accountId,
          )
          .map((mandate) => ({
            ...mandate,
            bankHolderFirstName: account.holderFirstName,
            bankHolderLastName: account.holderLastName,
            bankIban: account.iban,
          })),
      ),
    [bankAccounts, accountId],
  );
};

/**
 * The in-progress one-off (OOFF) mandate for a given prescription, if any — DRAFT
 * (created, signature still pending) or ACTIVE (signed, not yet collected). Lets a
 * bill row surface a "resume" action instead of restarting the one-off flow. Returns
 * the first match, or null. Reuses the same per-patient query (react-query dedup).
 */
export const usePendingOoffMandate = (
  patientId: number | undefined,
  rxId: string | undefined,
): MandateSummaryDto | null => {
  const { data: bankAccounts = [] } = useSepaBankAccounts(patientId ?? 0, patientId != null);

  return useMemo(
    () =>
      bankAccounts
        .flatMap((account) => account.mandates)
        .find(
          (mandate) =>
            mandate.sequenceType === MandateSequenceTypeEnum.Ooff &&
            mandate.rxId === rxId &&
            (mandate.status === MandateStatusEnum.Draft ||
              mandate.status === MandateStatusEnum.Active),
        ) ?? null,
    [bankAccounts, rxId],
  );
};
